import React from "react";
import { isEmpty } from "lodash";

import { ReferencePickerPlugin } from "@adsk/acc-reference-picker";
import { TOOL_IDS } from "@adsk/alloy-provider";
import { FanIcon } from "@adsk/alloy-react-icon";
import { useLocale } from "@adsk/alloy-react-locale";
import { TabTitle } from "@adsk/exo-skeleton-react";

import { AssetFilters, clients } from "../../index";
import { AssetDTO, AssetWithCategoryAndStatus } from "../../models";
import getParsedAssets from "../helpers/getParsedAssets";
import getUniqueValues from "../helpers/getUniqueValues";
import AssetsActivityCard from "./AssetsActivityCard";
import AssetsCustomDataView from "./AssetsCustomDataView";
import AssetsEntityBlock from "./AssetsEntityBlock";
import AssetsEntityMenuItem from "./AssetsEntityMenuItem";
import AssetsFilterPanel from "./AssetsFilterPanel";
import AssetsMissingEntities from "./AssetsMissingEntities";
import AssetsPickerEmptyState from "./AssetsPickerEmptyState";
import AssetsPickerNoSearchResults from "./AssetsPickerNoSearchResults";
import AssetsProvider from "./AssetsProvider";
import CustomTopBar from "./CustomTopBar";

const NestedOptionMenuLabel = () => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  return <>{T.translate("ASSETS")}</>;
};

const enrichAssetsWithCategoryAndStatus = async (
  projectId: string,
  assets: AssetDTO[]
): Promise<AssetWithCategoryAndStatus[]> => {
  if (assets.length === 0) {
    return [];
  }

  const uniqueCategoriesIds = getUniqueValues(assets, "categoryId");
  const uniqueStatusesIds = getUniqueValues(assets, "statusId");

  const [categories, statuses] = await Promise.all([
    clients
      .getCategoryClient(projectId)
      .list({
        internalId: uniqueCategoriesIds,
      })
      .catch(() => []),
    clients
      .getStatusSetClient(projectId)
      .batchGetStatuses(uniqueStatusesIds)
      .catch(() => []),
  ]);

  const statusSetAssignments = await clients
    .getCategoryClient(projectId)
    .batchGetStatusSetAssignments(
      categories.map((category) => category.internalId),
      true
    );

  return getParsedAssets(assets, categories, statuses, statusSetAssignments);
};

const plugin: ReferencePickerPlugin<AssetWithCategoryAndStatus> = {
  metadata: {
    domain: "autodesk-bim360-asset",
    type: "asset",
  },
  nestedOptions: {
    label: <NestedOptionMenuLabel />,
    key: "assets",
    nestedCategoryKey: "assets",
    renderIcon: FanIcon.renderIcon,
  },
  renderIcon: FanIcon.renderIcon,
  toolId: TOOL_IDS.ASSETS,
  showSearchBox: true,
  is3LOSupported: true,
  PluralText: ({ lowerCase }) => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    const result = T.translate("ASSETS");
    if (lowerCase && typeof result === "string") {
      return result.toLowerCase();
    }
    return result;
  },
  PickerModalTitle: ({ showTabTitle = true }) => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    const title = T.translate("ADD_ASSET_REFERENCE");
    return (
      <>
        {showTabTitle && <TabTitle value={title as string} />}
        {title}
      </>
    );
  },
  AddRelationshipsButtonText: ({ count }) => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    return T.translate("ADD_ASSETS", { context: count });
  },
  SelectedCount: ({ count }) => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    return T.translate("ASSETS_SELECTED", { context: count });
  },
  CustomRemoveEntityModalTitle: () => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    return T.translate("REMOVE_ASSET_REFERENCE_TITLE");
  },
  CustomRemoveEntityModalContent: () => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    return T.translate("REMOVE_ASSET_REFERENCE_CONTENT");
  },
  queryEntities: async ({ projectId, pageLimit, filters }) => {
    const {
      searchExpression,
      cursorState,
      categoryIds,
      includeSubCategories,
      statusIds,
      locationId,
      includeSublocations,
    } = filters;

    const categoryFilterKey = includeSubCategories
      ? "categoryIds"
      : "assignedCategoryIds";
    const includeFilters = {
      ...(!isEmpty(statusIds) ? { statusIds } : {}),
      ...(locationId
        ? {
            location: {
              id: locationId,
              includeSubLocations: includeSublocations,
            },
          }
        : {}),
      ...(!isEmpty(categoryIds) ? { [categoryFilterKey]: categoryIds } : {}),
    };

    const { results: assets, pagination } = await clients
      .getAssetClient(projectId)
      .query({
        assetFilters: {
          searchText: searchExpression,
          limit: pageLimit,
          ...includeFilters,
        } as AssetFilters,
        cursorState: cursorState as string | undefined,
      });

    if (assets.length === 0) {
      return { data: [] };
    }

    const parsedAssets = await enrichAssetsWithCategoryAndStatus(
      projectId,
      assets
    );

    return {
      data: parsedAssets,
      nextPageQuery: pagination.nextPost?.url
        ? {
            projectId,
            pageLimit,
            filters: {
              ...filters,
              cursorState: pagination.cursorState,
            },
          }
        : undefined,
    };
  },
  getEntitiesByIds: async ({ projectId, ids }) => {
    const assets = await clients
      .getAssetClient(projectId)
      .batchGet(ids, { includeDeleted: true });

    const parsedAssets = await enrichAssetsWithCategoryAndStatus(
      projectId,
      assets
    );

    return {
      data: parsedAssets,
    };
  },
  Provider: AssetsProvider,
  EntityBlock: AssetsEntityBlock,
  EntityMenuItem: AssetsEntityMenuItem,
  ActivityCard: AssetsActivityCard,
  CustomDataView: AssetsCustomDataView,
  CustomMissingEntities: AssetsMissingEntities,
  CustomPickerEmptyState: AssetsPickerEmptyState,
  CustomPickerNoSearchResults: AssetsPickerNoSearchResults,
  FiltersPanel: AssetsFilterPanel,
  CustomTopBar: CustomTopBar,
};

export default plugin;
